// The self-guided WonderLabs media companion. Only its presentation belongs
// here: AITutorApp still owns the existing PWO/video Ask AI request, screenshot,
// active scene, playback timestamp and transcript lookup.
import React, { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator, Animated, Image, ScrollView, Text, TextInput,
  TouchableOpacity, View,
} from "react-native";

import professorSwanImage from "../../../assets/professor_swan_transparent.png";
import useOpenAiChunkedSpeech from "../../lessonPlans/present/useOpenAiChunkedSpeech";
import { toPlainAnswer } from "../../lessonPlans/present/presentAskClient";
import { guardSwanAnswer, isSwanHelpRequest } from "../journeySwanResponseGuard";

export default function JourneySwanMediaShell({
  asset,
  stepId = null,
  inquiryGuard = null,
  history = [],
  loading = false,
  onAsk,
  language = "en",
  userId = "Guest",
  readAloudEnabled = true,
  videoPlaying = false,
  externalSpeaking = false,
  onReturnToJourney,
  onReadAloudEnabledChange,
  isDarkMode = false,
  t = (value) => value,
}) {
  const [draft, setDraft] = useState("");
  const avatarMotion = useRef(new Animated.Value(0)).current;
  const [reducedMotion, setReducedMotion] = useState(false);
  const speech = useOpenAiChunkedSpeech({ language, userId });
  const lastSpokenRef = useRef("");
  const interruptedAskRef = useRef(null);
  const isVideo = asset?.type === "video";
  const mediaKey = `${asset?.type || ""}:${asset?.videoId || asset?.pwoId || asset?.activityId || ""}`;
  const entries = (Array.isArray(history) ? history : []).filter(
    (entry) => entry?._swanMediaAsk === true &&
      entry?._swanMediaAssetKey === mediaKey &&
      String(entry?._swanMediaStepId || "") === String(stepId || "")
  );
  const latest = entries[0] || null;
  const question = String(latest?.question || "").trim();
  const scopedEntries = entries.filter((entry) =>
    String(entry?._swanMediaGuardBlockId || "") === String(inquiryGuard?.block_id || "")
  );
  const hintAttempt = scopedEntries.filter((entry) => isSwanHelpRequest(entry?.question)).length;
  const previousEntry = scopedEntries.find((entry) => entry !== latest && !entry?._isPending && entry?.answer);
  const answer = guardSwanAnswer(toPlainAnswer(String(latest?.answer || "")), inquiryGuard, question, {
    hintAttempt,
    previousAnswer: toPlainAnswer(String(previousEntry?.answer || "")),
  });
  const pending = Boolean(latest?._isPending || (latest && loading));
  const speaking = Boolean((speech.speaking || externalSpeaking) && !videoPlaying);
  const swanMotionState = speaking ? "speaking" : pending ? "thinking" : "listening";
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(Boolean(preference.matches));
    update();
    if (preference.addEventListener) {
      preference.addEventListener("change", update);
      return () => preference.removeEventListener("change", update);
    }
    preference.addListener?.(update);
    return () => preference.removeListener?.(update);
  }, []);
  useEffect(() => {
    avatarMotion.stopAnimation();
    avatarMotion.setValue(0);
    if (reducedMotion) return undefined;
    const duration = swanMotionState === "speaking" ? 600
      : swanMotionState === "thinking" ? 1100 : 2400;
    const loop = Animated.loop(Animated.sequence([
      Animated.timing(avatarMotion, { toValue: 1, duration, useNativeDriver: true }),
      Animated.timing(avatarMotion, { toValue: 0, duration, useNativeDriver: true }),
    ]));
    loop.start();
    return () => loop.stop();
  }, [avatarMotion, reducedMotion, swanMotionState]);
  const finished = Boolean(latest && !pending && answer);
  const useGuardedApprovedSpeech = Boolean(inquiryGuard?.protect_answer);
  const askKey = latest?._askRequestId || `${question}:${answer}`;
  // A useful, no-AI-call starting cue based on the asset the child opened;
  // never assume anything about the exact frame, scene or physical outcome.
  const mediaTitle = String(asset?.title || asset?.name || "").trim();
  const openingCue = isVideo
    ? (mediaTitle ? `Watch “${mediaTitle}” for one clue to your investigation. What do you notice?`
      : "Watch for one clue to your investigation. What do you notice?")
    : (mediaTitle ? `Explore “${mediaTitle}”. Start with the controls visible in the model. Ask me how to use this 3D activity if you are unsure.`
      : "Look for the model’s interaction controls. Ask me what to try first if you are unsure.");
  const targetedPwoCue = String(stepId || "") === "wind-car-explore"
    ? "Mystery Rover: rotate the model, open the parts icons, and find the motor shaft. See whether it connects to the wheels or the propeller."
    : String(stepId || "") === "wind-car-understand"
      ? "Parts investigation: tap each 3D part to learn its name. Compare its job with the actual kit before matching the components."
      : String(stepId || "") === "wind-car-apply"
        ? "Assembly investigation: examine each installation stage and keep the real batteries disconnected while matching the kit pieces."
        : openingCue;
  // Prefer the authored activity goal to guesses about a native 3D UI.
  const visibleOpeningCue = isVideo ? openingCue :
    (asset?.mission ? `${asset.mission} If the controls are unclear, ask Professor Swan how to begin.` : targetedPwoCue);
  const actionQuestion = isVideo
    ? t("What should I notice in this video?")
    : t("How do I use this 3D activity?");
  const ink = isDarkMode ? "#f8fafc" : "#16324f";
  const muted = isDarkMode ? "#cbd5e1" : "#475569";
  const border = isDarkMode ? "#334155" : "#dbe5ee";
  const paper = isDarkMode ? "#101e33" : "#ffffff";
  const panel = isDarkMode ? "#1e293b" : "#f5f9ff";
  const accent = isDarkMode ? "#7dd3fc" : "#0759a9";

  // Switching media must not leave the previous model/video answer talking.
  useEffect(() => {
    speech.stop();
    // A saved conversation can reappear when reopening a video/model;
    // showing it again should not automatically replay yesterday's speech.
    lastSpokenRef.current = latest?._askRequestId || "";
  }, [mediaKey, speech.stop]);

  // Video playback wins over speech, including a guarded reply still being
  // synthesized asynchronously. A later video pause must not replay that
  // interrupted answer; only a new question can start new spoken guidance.
  useEffect(() => {
    if (!isVideo || !videoPlaying) return;
    interruptedAskRef.current = askKey;
    speech.stop();
  }, [isVideo, videoPlaying, askKey, speech.stop]);

  // Never synthesize the unapproved raw model answer behind an evidence gate.
  // Read the finished guard-approved answer with the same OpenAI voice as Ask AI.
  useEffect(() => {
    if (!useGuardedApprovedSpeech || !finished || !readAloudEnabled || !speech.supported ||
        (isVideo && (videoPlaying || interruptedAskRef.current === askKey))) return;
    if (lastSpokenRef.current === askKey) return;
    lastSpokenRef.current = askKey;
    speech.speakText(answer);
  }, [answer, askKey, finished, readAloudEnabled, isVideo, videoPlaying,
      useGuardedApprovedSpeech, speech.speakText, speech.supported]);

  useEffect(() => {
    if (readAloudEnabled) return;
    speech.stop();
  }, [readAloudEnabled, speech.stop]);

  const submit = (value) => {
    const text = String(value || "").trim();
    if (!text || pending || !onAsk) return;
    speech.stop();
    setDraft("");
    // AITutorApp stops its own streaming audio before each new question.
    onAsk(text);
  };

  return (
    <View style={{
      flex: 1, minHeight: 0, overflow: "hidden", backgroundColor: paper,
      borderRadius: 14, borderWidth: 1, borderColor: border,
    }}>
      <View style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: border }}>
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <Text style={{ color: accent, fontWeight: "900", fontSize: 14 }}>
            {t("PROFESSOR SWAN")}
          </Text>
          {onReturnToJourney ? <TouchableOpacity accessibilityRole="button"
            accessibilityLabel={t("Back to this Journey activity")}
            onPress={onReturnToJourney}
            style={{ borderWidth: 1, borderColor: border, borderRadius: 9, padding: 8 }}>
            <Text style={{ color: accent, fontSize: 12, fontWeight: "800" }}>
              {t("← Back to activity")}
            </Text>
          </TouchableOpacity> : null}
        </View>
        <View style={{ flexDirection: "row", alignItems: "center", marginTop: 6 }}>
          <Animated.View style={{
            marginRight: 8,
            transform: [
              { translateY: avatarMotion.interpolate({ inputRange: [0, 1], outputRange: [0, speaking ? -4 : -2] }) },
              { rotate: avatarMotion.interpolate({ inputRange: [0, 1], outputRange: ["0deg", pending && !speaking ? "-5deg" : "2deg"] }) },
              { scale: avatarMotion.interpolate({ inputRange: [0, 1], outputRange: [1, speaking ? 1.035 : 1.01] }) },
            ],
          }}>
            <Image source={professorSwanImage} resizeMode="contain"
              style={{ width: 55, height: 74 }}
              accessibilityLabel={t("Professor Swan")}/>
          </Animated.View>
          <View style={{ flex: 1 }}>
            <Text style={{ color: ink, fontSize: 14, fontWeight: "800" }}>
              {t("Let's explore together!")}
            </Text>
            <Text style={{ color: muted, fontSize: 12, marginTop: 3 }}>
              {t(speaking ? "I'm speaking…" : pending ? "I'm thinking…" :
                (isVideo ? "Pause the video whenever something makes you curious."
                  : "Explore the model and ask me what you notice."))}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          onPress={() => {
            speech.stop();
            if (!readAloudEnabled) lastSpokenRef.current = "";
            onReadAloudEnabledChange?.(!readAloudEnabled);
          }}
          accessibilityRole="button"
          accessibilityLabel={t(readAloudEnabled ? "Turn off Professor Swan read aloud" : "Turn on Professor Swan read aloud")}
          style={{ alignSelf: "flex-start", marginTop: 4, paddingVertical: 5 }}
        >
          <Text style={{ color: accent, fontWeight: "700", fontSize: 12 }}>
            {t(readAloudEnabled ? "Read aloud: On" : "Read aloud: Off")}
          </Text>
        </TouchableOpacity>
      </View>

      {/* One visible question and one visible answer, just like inline Swan chat.
          No History/Chat/Suggested Questions tabs or unrelated references. */}
      <ScrollView
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={{ paddingHorizontal: 12, paddingVertical: 14, flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
      >
        {!isVideo ? <View style={{ borderRadius: 10, padding: 12, backgroundColor: panel,
          borderWidth: 1, borderColor: border, marginBottom: 12 }}>
          <Text style={{ color: accent, fontSize: 12, fontWeight: "900" }}>
            {t("START HERE IN THE 3D MODEL")}
          </Text>
          <Text style={{ color: ink, lineHeight: 21, fontSize: 14, marginTop: 6 }}>
            {t(visibleOpeningCue)}
          </Text>
          <TouchableOpacity accessibilityRole="button"
            accessibilityLabel={t("Read my 3D mission aloud")}
            onPress={() => speech.speakText(t(visibleOpeningCue))}
            style={{ alignSelf: "flex-start", marginTop: 10 }}>
            <Text style={{ color: accent, fontWeight: "800", fontSize: 13 }}>
              {t("🔊 Hear my exploration mission")}
            </Text>
          </TouchableOpacity>
        </View> : null}
        {latest ? (
          <View>
            <Text style={{ color: accent, fontWeight: "800", fontSize: 12, marginBottom: 7 }}>
              {t("You asked")}
            </Text>
            <View style={{ backgroundColor: panel, borderRadius: 10, padding: 12, borderWidth: 1, borderColor: border }}>
              <Text style={{ color: ink, fontSize: 14, fontWeight: "600" }}>{question}</Text>
            </View>
            <Text style={{ color: accent, fontWeight: "800", fontSize: 12, marginTop: 15, marginBottom: 7 }}>
              {t("Professor Swan")}
            </Text>
            {pending && !answer ? (
              <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
                <ActivityIndicator size="small" color={accent}/>
                <Text style={{ color: muted }}>{t("Thinking...")}</Text>
              </View>
            ) : (
              <Text style={{ color: ink, lineHeight: 21, fontSize: 14 }}>
                {answer || t("I couldn't answer that yet. Try asking another way.")}
              </Text>
            )}
            {pending && answer ? <ActivityIndicator size="small" color={accent}/> : null}
          </View>
        ) : (
          <Text style={{ color: muted, fontSize: 14, lineHeight: 21 }}>
            {t(isVideo ? "Watch for evidence. Ask about anything you notice." : "Explore the controls in the 3D activity. Ask Swan how to begin or what to investigate.")}
          </Text>
        )}
      </ScrollView>

      <View style={{ padding: 12, borderTopWidth: 1, borderTopColor: border, gap: 9 }}>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {[actionQuestion, t("Give me a hint.")].map((item) => (
            <TouchableOpacity key={item} onPress={() => submit(item)}
              disabled={pending} accessibilityRole="button"
              style={{ borderRadius: 9, backgroundColor: panel, borderWidth: 1, borderColor: border, padding: 8, opacity: pending ? 0.6 : 1 }}>
              <Text style={{ color: accent, fontSize: 12, fontWeight: "700" }}>{item}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={() => submit(draft)}
            editable={!pending}
            placeholder={t("Ask Professor Swan...")}
            placeholderTextColor={muted}
            accessibilityLabel={t("Ask Professor Swan a question")}
            style={{ flex: 1, borderColor: border, borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, backgroundColor: panel, color: ink, fontSize: 14 }}
          />
          <TouchableOpacity
            onPress={() => submit(draft)} disabled={pending || !draft.trim()}
            accessibilityRole="button" accessibilityLabel={t("Ask Professor Swan")}
            style={{ borderRadius: 10, paddingVertical: 12, paddingHorizontal: 14, backgroundColor: accent, opacity: pending || !draft.trim() ? 0.5 : 1 }}
          >
            <Text style={{ color: "#ffffff", fontWeight: "800" }}>➤</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}
